/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { message, Spin, Tag, Tooltip } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { actionPlanApi } from '@/services/action-plan';
import { congviecduanApi } from '@/services/action-plan/congviecduan';
import { usePrivateStore } from '@/store/usePrivateStore';
import { DuAnCongViecChiTietView, DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import type { tabletansuat } from '@/types/performance/bangtuansuat';
import { itemDayRange, itemKetThucDayKey, toDayKey } from '@/components/common/ActionPlan/utils/baoCaoNhanhVtnTree';
import { formatDuAnCongViecDatetimeDisplayText } from '@/components/common/ActionPlan/utils/du-an-utils';
import { CONSTANTS } from '@/utils/constants';
import type { TaskPriority } from '@/components/DashBoard/DashBoardOverview/DailyWork';
import { useAuthStore } from '@/store/useAuthStore';
import { ModalFormDetail } from '@/components/common/ActionPlan/CongViecDuAn/Modals/ModalDetail';
import { useActionPlanPermission } from '@/components/common/ActionPlan/utils/permissionUtils';
import { ChevronDown, ChevronUp } from '../Icons/IconMenu';
import { useGetPhanHe } from '@/hooks/useGetPhanHe';

const PAGE_SIZE = 15;
const SHOW_DELAY_MS = 3000;
const ICON_SIZE = 20;
/** Giữ đóng/mở panel (localStorage) khi Layout remount theo `controller`. */
const MINIMIZED_STORAGE_KEY = CONSTANTS.ACTION_PLAN.MINIMIZED_STORAGE_KEY;

function readMinimizedFromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(MINIMIZED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMinimizedToStorage(minimized: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(MINIMIZED_STORAGE_KEY, minimized ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
}
function getTabSegment(tab?: string): string {
  if (tab === '1') return 'pt';
  if (tab === '2') return 'dg';
  if (tab === '3') return 'ht';
  return 'pt';
}

/** Same as ViecTrongNgay: merge kanban from list row into chitiet payload */
function mergeKanbanFromRecord(detailData: any, record: any): void {
  if (!record?.congViec) return;
  const duAnCongViec = detailData?.duAnCongViec;
  const congViec = duAnCongViec?.congViec;
  if (!congViec || !record.congViec) return;

  if (!duAnCongViec) return;
  if (!duAnCongViec.congViec) duAnCongViec.congViec = {};
  if (record.congViec.kanBanViTri !== undefined) {
    duAnCongViec.congViec.kanBanViTri = record.congViec.kanBanViTri ?? congViec.kanBanViTri ?? 0;
  }
  const hasKanbans =
    record.congViec.kanbans &&
    Array.isArray(record.congViec.Kanbans) &&
    record.congViec.Kanbans.length > 0;
  const targetEmpty =
    !congViec.Kanbans || !Array.isArray(congViec.Kanbans) || congViec.Kanbans.length === 0;
  if (hasKanbans) duAnCongViec.congViec.kanbans = record.congViec.kanbans;
  else if (targetEmpty) duAnCongViec.congViec.kanbans = record.congViec.kanbans || [];
}

function getAllTienDos(record: DuAnCongViecTree): any[] {
  const cv = record.congViec as any;
  const fromCv = cv?.tienDos || cv?.TienDos;
  if (Array.isArray(fromCv) && fromCv.length > 0) return fromCv;
  const fromRow = record.listBaoCaoTrongNgay;
  if (Array.isArray(fromRow) && fromRow.length > 0) return fromRow;
  return [];
}

function getTodayTienDos(record: DuAnCongViecTree): any[] {
  const today = dayjs().startOf('day');
  return getAllTienDos(record).filter((r) => r?.ngay != null && dayjs(r.ngay).isSame(today, 'day'));
}

/** 1 = Theo ngày, 4 = Cuối kỳ (khớp FormInfomation / BE TanSuatDuAnEnum). */
const TAN_SUAT_NGAY = 1;
const TAN_SUAT_CUOI_KY = 4;

function getCongViecTanSuatCode(record: DuAnCongViecTree): number {
  const cv = record.congViec as any;
  const raw = cv?.tanSuat ?? cv?.TanSuat;
  const n = Number(raw);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Các dòng listTanSuat tương ứng kỳ báo cáo “hôm nay” — align BE:
 * - Cuối kỳ: một mốc theo ngày kết thúc mới nhất (nhiều kỳ → lấy ketthuc lớn nhất).
 * - Theo ngày: dòng có ngày = hôm nay.
 * - Khác: kỳ có batdau <= hôm nay <= ketthuc; ưu tiên `current`, không thì kỳ có ketthuc cuối trong các kỳ chứa hôm nay.
 */
function getTodayReportRelevantTanSuatRows(record: DuAnCongViecTree): Partial<tabletansuat>[] {
  const raw = record.listTanSuat;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const code = getCongViecTanSuatCode(record);
  const todayKey = toDayKey(new Date());

  if (code === TAN_SUAT_CUOI_KY) {
    if (raw.length === 1) return raw;
    const scored = raw.map((row) => {
      const k = itemKetThucDayKey(row as any);
      const r = itemDayRange(row as any);
      const endKey = k || r?.end || '';
      return { row, endKey };
    });
    scored.sort((a, b) => b.endKey.localeCompare(a.endKey));
    const best = scored.find((s) => s.endKey !== '');
    return best ? [best.row] : [raw[raw.length - 1]];
  }

  if (!todayKey) return [];

  if (code === TAN_SUAT_NGAY) {
    return raw.filter((row) => {
      if (row?.ngay != null) {
        return toDayKey(row.ngay as string | Date) === todayKey;
      }
      const range = itemDayRange(row as any);
      return !!(range && range.start === todayKey && range.end === todayKey);
    });
  }

  const inPeriod = raw.filter((row) => {
    const range = itemDayRange(row as any);
    return !!(range && todayKey >= range.start && todayKey <= range.end);
  });
  if (inPeriod.length === 0) {
    const onlyCurrent = raw.filter((row) => row?.current === true);
    return onlyCurrent.length ? onlyCurrent : [];
  }
  const marked = inPeriod.find((r) => r?.current === true);
  if (marked) return [marked];
  if (inPeriod.length === 1) return inPeriod;
  const withEnd = inPeriod.map((row) => ({
    row,
    end: itemKetThucDayKey(row as any) || itemDayRange(row as any)?.end || '',
  }));
  withEnd.sort((a, b) => a.end.localeCompare(b.end));
  return [withEnd[withEnd.length - 1].row];
}

function isTanSuatPeriodComplete(row: Partial<tabletansuat>): boolean {
  if (row.trangThai === true) return true;
  const chi = row.chiTieu;
  const th = row.thucTe;
  if (chi == null || th == null) return false;
  const nChi = Number(chi);
  const nTh = Number(th);
  if (Number.isNaN(nChi) || Number.isNaN(nTh)) return false;
  return nTh >= nChi;
}

/** Giống Razor: có dòng tiến độ ngày hôm nay hoặc kỳ báo cáo (theo tần suất + kết thúc kỳ) → hiện nút tick. */
function showHoanThanhTick(record: DuAnCongViecTree): boolean {
  if (getCongViecTanSuatCode(record) === TAN_SUAT_CUOI_KY && Array.isArray(record.listTanSuat) && record.listTanSuat.length > 0) {
    return true;
  }
  return getTodayTienDos(record).length > 0 || getTodayReportRelevantTanSuatRows(record).length > 0;
}

/** Đã hoàn thành / đạt chỉ tiêu cho đúng kỳ báo cáo hôm nay (theo tần suất), không nhầm kỳ khác. */
function isCompletedToday(record: DuAnCongViecTree): boolean {
  if (getCongViecTanSuatCode(record) === TAN_SUAT_CUOI_KY) {
    const rows = getTodayReportRelevantTanSuatRows(record);
    return rows.some(isTanSuatPeriodComplete);
  }
  if (
    getTodayTienDos(record).some((r) => {
      const ct = r.chiTieu ?? r.ChiTieu;
      const ht = r.hoanThanh ?? r.HoanThanh;
      if (ct == null || ht == null) return false;
      return Number(ct) === Number(ht);
    })
  ) {
    return true;
  }
  return getTodayReportRelevantTanSuatRows(record).some(isTanSuatPeriodComplete);
}

function formatTaskDateRange(record: DuAnCongViecTree): string {
  const cv = record.congViec;
  const start = cv?.ngayBatDau;
  const end = cv?.ngayKetThuc;
  if (start && end) {
    const s = dayjs(start);
    const e = dayjs(end);
    const compact = formatDuAnCongViecDatetimeDisplayText(s, e);
    if (compact) return compact;
  }
  return record.thoiGian || '';
}

function normalizeTaskPriority(kanBanMucDoUuTien?: string | null): TaskPriority {
  const raw = (kanBanMucDoUuTien || '').trim().toLowerCase();
  if (raw === 'high') return 'high';
  if (raw === 'medium') return 'medium';
  return 'low';
}

function normalizeLayoutPath(pathname: string): string {
  const pathOnly = (pathname || '').split('?')[0].split('#')[0];
  if (pathOnly === '/' || pathOnly === '') return '';
  return pathOnly.replace(/^\/+|\/+$/g, '');
}

/** Dashboard BSC: `/` (pages/index) — route mặc định localhost:3000/ */
function isBscDashboardPath(pathname: string): boolean {
  const clean = normalizeLayoutPath(pathname);
  return clean === '' || clean === 'index';
}

/** Chỉ BSC + dashboard (/) / du-an / cong-viec-du-an. Action Plan V2 tạm ẩn (đè board Kanban / lưới). */
export function shouldShowFloatingViecTrongNgayPanel(pathname: string, phanHe: string): boolean {
  if ((phanHe || '').trim().toLowerCase() !== CONSTANTS.PHAN_HE.BSC) return false;
  if (isBscDashboardPath(pathname)) return true;
  const firstSegment = normalizeLayoutPath(pathname).split('/').filter(Boolean)[0] ?? '';
  return firstSegment === 'du-an' || firstSegment === 'cong-viec-du-an';
}

export const FloatingViecTrongNgayPanel: React.FC = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const phanHe = useGetPhanHe();
  const routePath = router.pathname || router.asPath || '/';
  const enabled = shouldShowFloatingViecTrongNgayPanel(routePath, phanHe || CONSTANTS.PHAN_HE.BSC);

  const { idSelect } = usePrivateStore().getTreeSelectDepartment();
  const giaiDoan = usePrivateStore((s) => s.giaiDoan);
  const dateChoiceDefault = usePrivateStore((s) => s.dateChoiceDefault);
  const profile = useAuthStore((s) => s.profile);
  const { canEdit } = useActionPlanPermission();

  const year = giaiDoan?.year ?? new Date().getFullYear();
  const dateRange = giaiDoan?.dateChoice || dateChoiceDefault || '';

  const hienThi = '1';
  const activeTab = '1';

  const [activated, setActivated] = useState(false);
  const [minimized, setMinimizedState] = useState(() => readMinimizedFromStorage());
  const minimizedRef = useRef(minimized);
  minimizedRef.current = minimized;

  const setMinimized = useCallback((next: boolean) => {
    setMinimizedState(next);
    minimizedRef.current = next;
    writeMinimizedToStorage(next);
  }, []);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<DuAnCongViecTree[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [markingId, setMarkingId] = useState<string | null>(null);

  const [openModalDetail, setOpenModalDetail] = useState(false);
  const [modalDetailData, setModalDetailData] = useState<DuAnCongViecChiTietView | null>(null);
  const [loadingModalDetail, setLoadingModalDetail] = useState(false);
  const [detailMeta, setDetailMeta] = useState<{ idDuAn: string; duan: DuAnCongViecTree | null } | null>(
    null
  );
  const lastDetailRecordRef = useRef<DuAnCongViecTree | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setActivated(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const fetchList = useCallback(async () => {
    if (!enabled || minimizedRef.current) {
      return;
    }
    if (!idSelect) {
      setList([]);
      return;
    }
    setLoading(true);
    try {
      const response: any = await actionPlanApi.getViecTrongNgay({
        id: idSelect || '',
        idChoice: idSelect || '',
        year,
        dateChoice: undefined,
        hienThi: '',
        tabActive: '',
        isDetail: false,
        phanHe: CONSTANTS.PHAN_HE.BSC,
        showBaoCao: true,
      });
      if (response?.success && response?.data) {
        setList(Array.isArray(response.data) ? response.data : []);
      } else {
        setList([]);
      }
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, idSelect, year]);

  useEffect(() => {
    if (!enabled || !activated || minimizedRef.current) return;
    void fetchList();
  }, [enabled, activated, minimized, idSelect, year, fetchList]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [list]);

  const handleModalDetailClose = useCallback(() => {
    setOpenModalDetail(false);
    setModalDetailData(null);
    setDetailMeta(null);
    lastDetailRecordRef.current = null;
  }, []);

  const handleSaveSuccess = useCallback(() => {
    void fetchList();
    handleModalDetailClose();
  }, [fetchList, handleModalDetailClose]);

  const handleOpenDetailByRecord = useCallback(
    async (record: DuAnCongViecTree) => {
      if (record.congViec.stateApproved !== 1) {
        if (record.congViec.nguoiTao !== profile?.id) {
          return;
        }
      }

      const idDuAnForApi =
        record.congViec?.duAnID || (record.congViec?.isDuAn ? record.congViec.id : '') || '';

      if (!record.congViec?.id || !idDuAnForApi) {
        setOpenModalDetail(true);
        setModalDetailData(null);
        setDetailMeta(null);
        return;
      }

      lastDetailRecordRef.current = record;
      setDetailMeta({ idDuAn: idDuAnForApi, duan: null });
      setLoadingModalDetail(true);
      setOpenModalDetail(true);

      try {
        const idChoiceFromPhuTrach =
          (record.congViec?.nguoiPhuTrachID || '').trim() || idSelect || '';
        const chitietResponse: any = await congviecduanApi.chitiet({
          id: idChoiceFromPhuTrach,
          idChoice: idChoiceFromPhuTrach,
          idDuAn: idDuAnForApi,
          idMucTieu: record.congViec?.mtctid || '0',
          idCongViec: record.congViec.id,
          year,
          dateChoice: dateRange || undefined,
          hienThi, 
          tabActive: getTabSegment(activeTab),
          isDetail: true,
          phanHe: CONSTANTS.PHAN_HE.BSC,
        });

        if (chitietResponse?.data) {
          const detailData = chitietResponse.data as DuAnCongViecChiTietView;
          mergeKanbanFromRecord(detailData, record);
          setModalDetailData(detailData);
        } else {
          setModalDetailData(null);
        }
      } catch (error) {
        console.error('FloatingViecTrongNgay chitiet:', error);
        setModalDetailData(null);
      } finally {
        setLoadingModalDetail(false);
      }
    },
    [idSelect, year, dateRange, hienThi, activeTab, profile?.id]
  );

  const handleMarkHoanThanhTrongNgay = useCallback(
    async (e: React.MouseEvent, record: DuAnCongViecTree) => {
      e.preventDefault();
      e.stopPropagation();
      const id = record.congViec?.id;
      if (!id) return;
      const clearing = isCompletedToday(record);
      setMarkingId(id);
      try {
        const res: any = await congviecduanApi.updateHoanThanhTrongNgay({
          congViecID: id,
          phanHe: CONSTANTS.PHAN_HE.BSC,
          state: clearing ? null : 'hoanthanh',
        });
        if (res?.success !== false) {
          message.success(t('bsc.msg.capNhatThanhCong', 'Cập nhật thành công'));
          await fetchList();
        } else {
          message.error(res?.message || t('bsc.msg.capNhatThatBai', 'Cập nhật thất bại'));
        }
      } catch (err) {
        console.error('updateHoanThanhTrongNgay', err);
        message.error(t('bsc.msg.capNhatThatBai', 'Cập nhật thất bại'));
      } finally {
        setMarkingId(null);
      }
    },
    [fetchList, t]
  );

  const slice = useMemo(() => list.slice(0, visibleCount), [list, visibleCount]);
  const hasMore = visibleCount < list.length;

  if (!enabled || !activated) return null;

  const panelTitle = t('bsc.btn.viectrongngay', 'Việc trong ngày');
  const priorityStyles = CONSTANTS.ACTION_PLAN.PRIORITY_TAG_PROPS;

  const panelCard = minimized ? null : (
    <div
      className={classNames(
        'floating-vtn-slide-up',
        'fixed z-[200] w-[min(100vw-1.25rem,420px)] overflow-hidden rounded-xl',
        'border border-gray-200/90 bg-white',
        'shadow-[0_10px_40px_-4px_rgba(12,40,68,0.14),0_4px_16px_-4px_rgba(12,40,68,0.08)]',
        'bottom-4 right-2 md:bottom-4 md:right-2'
      )}
      role="complementary"
      aria-label={panelTitle}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 border-b border-gray-100/90 bg-gradient-to-r from-[#f8fafc] via-white to-[#f4f8fc] px-4 py-3 text-left transition-colors hover:from-gray-50 hover:via-white"
        onClick={() => setMinimized(true)}
        aria-label={t('bsc.aria.thuGonViecTrongNgay', 'Thu gọn việc trong ngày')}
      >
        <span className="text-[15px] font-semibold tracking-tight text-[#1B252E]">{panelTitle}</span>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/80 text-gray-500 shadow-sm ring-1 ring-gray-100">
        <ChevronDown width={ICON_SIZE} height={ICON_SIZE}/>
        </span>
      </button>

      <div className="max-h-[min(60vh,340px)] overflow-auto px-0 pb-1">
        {loading ? (
          <div className="flex justify-center py-10">
            <Spin size="small" />
          </div>
        ) : (
          <>
            <table className="w-full table-fixed border-collapse text-[12px] leading-tight">
              <colgroup>
                <col className="min-w-0" style={{ width: '56%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr className="bg-[#f4f6f8] text-[12px] font-medium tracking-wide text-[#637381] sticky z-10 top-0">
                  <th className="py-2 pl-4 pr-1 text-left font-medium">
                    {t('bsc.label.tencongviec', 'Tên công việc')}
                  </th>
                  <th className="px-1 py-2 text-center font-medium">
                    {t('bsc.label.uutien', '\u01AFu ti\u00EAn')}
                  </th>
                  <th className="whitespace-nowrap py-2 pl-1 pr-2 text-right font-medium">
                    {t('vanhoa.thoigian', 'Th\u1EDDi gian')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {slice.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-2 py-8 text-center text-[12px] text-[#637381]">
                      {t('bsc.label.khongcoviectrongngay', 'Không có việc trong ngày nào')}
                    </td>
                  </tr>
                ) : (
                  slice.map((item, idx) => {
                    const cv = item.congViec;
                    const prio = normalizeTaskPriority(cv?.kanBanMucDoUuTien);
                    const tagProps = priorityStyles[prio];
                    const doneToday = isCompletedToday(item);
                    const showTick = showHoanThanhTick(item);
                    const rowNum = idx + 1;
                    const busy = markingId === cv?.id;

                    return (
                      <tr
                        key={cv?.id || idx}
                        className="transition-colors hover:bg-[rgba(12,104,233,0.04)]"
                      >
                        <td className="min-w-0 py-2 pl-4 pr-4 align-middle text-left">
                          <div className="flex min-w-0 items-center gap-1.5">
                            {showTick ? (
                              <Tooltip
                                title={
                                  doneToday
                                    ? t('bsc.hint.unmarkCompleteToday', 'Bỏ hoàn thành trong ngày')
                                    : t('bsc.hint.markCompleteToday', 'Đánh dấu hoàn thành trong ngày')
                                }
                              >
                                <button
                                  type="button"
                                  disabled={busy}
                                  className="shrink-0 border-0 bg-transparent p-0 leading-none text-gray-400 hover:text-[#0C68E9] disabled:opacity-50"
                                  aria-label={
                                    doneToday
                                      ? t('bsc.aria.boHoanThanhTrongNgay', 'Bỏ hoàn thành trong ngày')
                                      : t('bsc.aria.hoanThanhTrongNgay', 'Hoàn thành trong ngày')
                                  }
                                  onClick={(e) => void handleMarkHoanThanhTrongNgay(e, item)}
                                >
                                  <CheckCircleFilled
                                    className={classNames(
                                      'text-[12px] transition-colors',
                                      doneToday ? '!text-[#22c55e]' : '!text-gray-300'
                                    )}
                                  />
                                </button>
                              </Tooltip>
                            ) : null}
                            <span
                              role="link"
                              tabIndex={0}
                              className={classNames(
                                'min-w-0 flex-1 cursor-pointer break-words text-left text-[10px] font-medium no-underline',
                                'text-[#637381] hover:text-[#0C68E9]',
                                doneToday && 'hover:text-[#637381]'
                              )}
                              onClick={() => void handleOpenDetailByRecord(item)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  void handleOpenDetailByRecord(item);
                                }
                              }}
                            >
                              <span className="text-[#637381]">{rowNum}.</span>{' '}
                              <span
                                className={classNames(
                                  doneToday &&
                                    'text-[#919EAB] line-through decoration-[#919EAB]'
                                )}
                              >
                                {cv?.tenCongViec || ''}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="px-1 py-2 align-middle">
                          <div className="flex justify-center">
                            <Tag
                              color={tagProps.color}
                              className={classNames(
                                tagProps.className,
                                '!max-w-none !px-2 !py-px !text-[9px] !font-semibold !uppercase !tracking-wide'
                              )}
                            >
                              {t(`bsc.text.mucdouutien.${prio}`, tagProps.label)}
                            </Tag>
                          </div>
                        </td>
                        <td className="py-2 pl-1 pr-4 align-middle text-right">
                          <span className="tabular-nums text-[10px] font-medium leading-none text-[#637381]">
                            {formatTaskDateRange(item)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            {hasMore ? (
              <div className="flex justify-center border-t border-gray-50 px-3 py-1">
                <button
                  type="button"
                  className="rounded-xl bg-[#f4f6f8] px-4 py-1.5 !text-[12px] font-medium text-[#0C68E9] transition-colors hover:bg-[#e8ecf1]"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                >
                  {t('bsc.btn.xemthem', 'Xem thêm')} ({list.length - visibleCount})
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );

  const fab = minimized ? (
    <Tooltip title={panelTitle} placement="left">
      <button
        hidden={loading}
        type="button"
        className={classNames(
          'floating-vtn-slide-up',
          'fixed z-[200] flex h-8 w-8 items-center justify-center text-white shadow-lg',
          '-bottom-1 right-1 md:-bottom-1 md:-right-1',
          'border-0 outline-none hover:opacity-90 focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2'
        )}
        style={{
          backgroundColor: '#1677ff',
          borderRadius: '6px',
          borderBottomRightRadius: '0',
          borderBottomLeftRadius: '0',
          boxShadow: '0 4px 14px rgba(22, 119, 255, 0.45)',
        }}
        aria-label={panelTitle}
        onClick={() => setMinimized(false)}
      >
        <ChevronUp width={ICON_SIZE} height={ICON_SIZE}/>
      </button>
    </Tooltip>
  ) : null;

  return (
    <>
      {fab}
      {panelCard}
      <ModalFormDetail
        open={openModalDetail}
        handleclose={handleModalDetailClose}
        data={modalDetailData}
        loading={loadingModalDetail}
        listMucTieu={undefined}
        idMucTieu={lastDetailRecordRef.current?.congViec?.mtctid || '0'}
        idDuAn={detailMeta?.idDuAn}
        duan={detailMeta?.duan ?? undefined}
        kanbans={[]}
        onSaveSuccess={handleSaveSuccess}
        onSaveNganSachSuccess={handleSaveSuccess}
        showFooter
        readOnly={!canEdit(modalDetailData?.duAnCongViec?.congViec)}
      />
    </>
  );
};
